/**
 * Popup content renderer using DOM APIs.
 */
import { el, empty } from '../utils/dom.js';
import { getLabels, getConfig } from '../config.js';

function priceNode(html, className) {
	const wrap = el('div', { className });
	if (html) {
		wrap.innerHTML = html;
	}
	return wrap;
}

/**
 * Stock line matching the wishlist table / TI markup.
 *
 * In stock:  <p class="stock in-stock"><span><i class="ftinvwl ftinvwl-check"></i></span><span>In stock</span></p>
 * Out of stock: <p class="stock out-of-stock"><span><i class="ftinvwl ftinvwl-times"></i></span><span>Out of stock</span></p>
 *
 * Wrapped in `.tinv-wishlist.woocommerce .product-stock` so TI CSS hides WooCommerce’s
 * `.stock::before` and applies the correct icon colours.
 *
 * @param {{ className?: string, text?: string, inStock?: boolean, backordersAllowed?: boolean }} status
 */
export function stockStatusNode(status = {}) {
	const labels = getLabels();
	let className = status.className || '';
	let text = status.text || '';

	if (!className || !text) {
		if (status.inStock) {
			className = 'in-stock';
			text = labels.inStock || 'In stock';
		} else if (status.backordersAllowed) {
			className = 'available-on-backorder';
			text = labels.backorder || 'Available on backorder';
		} else {
			className = 'out-of-stock';
			text = labels.outOfStock || 'Out of stock';
		}
	}

	// TI uses check for in-stock, times (X) for everything else.
	const icon =
		className === 'in-stock' ? 'ftinvwl ftinvwl-check' : 'ftinvwl ftinvwl-times';

	return el('div', { className: 'tinv-wishlist woocommerce' }, [
		el('div', { className: 'product-stock' }, [
			el('p', { className: `stock ${className}` }, [
				el('span', {}, [
					el('i', { className: icon, 'aria-hidden': 'true' }),
				]),
				el('span', { text }),
			]),
		]),
	]);
}

/**
 * Quick Buy modal: purchasable packs only, between the lowest and highest
 * purchasable seed counts. Out-of-stock packs never appear in the archive modal
 * (single-product pack selector handles disabled OOS separately).
 */
export function visibleVariations(variations) {
	const list = (variations || []).filter((v) => v && v.purchasable);
	const purchasableCounts = list
		.map((v) => Number(v.seed_count))
		.filter((n) => Number.isFinite(n) && n > 0);

	if (!purchasableCounts.length) {
		return list;
	}

	const min = Math.min(...purchasableCounts);
	const max = Math.max(...purchasableCounts);

	return list.filter((v) => {
		const count = Number(v.seed_count);
		if (!Number.isFinite(count) || count <= 0) {
			return true;
		}
		return count >= min && count <= max;
	});
}

export function renderLoading(container) {
	empty(container);
	const labels = getLabels();
	container.appendChild(
		el('div', { className: 'bc-quick-buy-modal__loading is-visible' }, [
			el('span', { className: 'bc-quick-buy-modal__spinner', 'aria-hidden': 'true' }),
			el('span', { text: labels.loading || 'Loading pack options…' }),
		])
	);
}

export function renderUnsupported(container, options) {
	empty(container);
	const labels = getLabels();
	container.appendChild(buildHeader(options, null));
	container.appendChild(
		el('p', {
			className: 'bc-quick-buy-modal__message',
			text: (options.messages && options.messages.instruction) || labels.unsupported,
		})
	);
	container.appendChild(
		el('a', {
			className: 'bc-quick-buy-modal__view-full',
			href: options.permalink || '#',
			text: labels.viewFull || 'More Info',
			'data-action': 'view-full',
		})
	);
}

export function renderUnavailable(container, options) {
	empty(container);
	const labels = getLabels();
	container.appendChild(
		buildHeader(options, {
			className: 'out-of-stock',
			text: labels.outOfStock || 'Out of stock',
		})
	);

	const actions = el('div', { className: 'bc-quick-buy-modal__actions' });

	if (options.stock_alert_available) {
		actions.appendChild(
			el('button', {
				type: 'button',
				className: 'bc-quick-buy-modal__notify',
				text: labels.notify || 'Notify me',
				'data-action': 'stock-alert',
			})
		);
	}

	actions.appendChild(
		el('a', {
			className: 'bc-quick-buy-modal__view-full',
			href: options.permalink || '#',
			text: labels.viewFull || 'More Info',
			'data-action': 'view-full',
		})
	);

	container.appendChild(actions);
}

export function renderPackChoices(container, options, selectedVariationId) {
	empty(container);
	const labels = getLabels();
	const config = getConfig();
	const variations = visibleVariations(options.variations);

	container.appendChild(
		buildHeader(options, {
			className: 'in-stock',
			text: labels.inStock || 'In stock',
		})
	);

	const instruction = (options.messages && options.messages.instruction) || labels.choosePack;
	container.appendChild(
		el('p', {
			className: 'bc-quick-buy-modal__instruction',
			id: 'bc-quick-buy-modal-instruction',
			text: instruction,
		})
	);

	const packs = el('div', {
		className: 'bc-quick-buy-modal__packs',
		role: 'group',
		'aria-labelledby': 'bc-quick-buy-modal-instruction',
	});

	variations.forEach((variation) => {
		// Modal never renders out-of-stock packs.
		if (!variation.purchasable) {
			return;
		}
		const selected = Number(variation.variation_id) === Number(selectedVariationId);
		const classes = ['bc-quick-buy-modal__pack'];
		if (selected) {
			classes.push('is-selected');
		}
		if (variation.sale_price_html) {
			classes.push('is-sale');
		}

		const children = [
			el('span', { className: 'bc-quick-buy-modal__pack-label', text: variation.label || '' }),
		];

		if (config.showPriceInPack !== false && variation.price_html) {
			// Per-seed stays in the summary row to keep pack buttons compact.
			children.push(priceNode(variation.price_html, 'bc-quick-buy-modal__pack-price'));
		}

		const packBtn = el('button', {
			type: 'button',
			className: classes.join(' '),
			'data-variation-id': String(variation.variation_id),
			'data-action': 'select-pack',
			'aria-pressed': selected ? 'true' : 'false',
		}, children);

		packs.appendChild(el('div', { className: 'bc-quick-buy-modal__pack-row' }, [packBtn]));
	});

	container.appendChild(packs);

	const selected = variations.find(
		(v) => Number(v.variation_id) === Number(selectedVariationId)
	);

	container.appendChild(buildPriceSummary(selected, labels, config));

	const actions = el('div', { className: 'bc-quick-buy-modal__actions' }, [
		el('button', {
			type: 'button',
			className: 'bc-quick-buy-modal__add',
			'data-action': 'add-to-basket',
			disabled: selected ? null : '',
			'aria-disabled': selected ? 'false' : 'true',
			text: labels.addToBasket || 'Add to basket',
			'aria-label': selected
				? `${labels.addToBasket || 'Add to basket'}: ${selected.label}`
				: labels.addToBasket || 'Add to basket',
		}),
		el('a', {
			className: 'bc-quick-buy-modal__view-full',
			href: options.permalink || '#',
			text: labels.viewFull || 'More Info',
			'data-action': 'view-full',
		}),
	]);

	container.appendChild(actions);
}

function buildPriceSummary(selected, labels, config) {
	const priceArea = el('div', { className: 'bc-quick-buy-modal__price-area' });

	if (!selected) {
		priceArea.appendChild(
			el('p', {
				className: 'bc-quick-buy-modal__price-placeholder',
				text: labels.choosePack || 'Choose a pack size',
			})
		);
		return priceArea;
	}

	const row = el('div', { className: 'bc-quick-buy-modal__price-row' });

	const priceBlock = el('div', { className: 'bc-quick-buy-modal__price-block' });
	const onSale = Boolean(selected.sale_price_html && selected.regular_price_html);

	if (onSale) {
		priceBlock.appendChild(
			priceNode(selected.regular_price_html, 'bc-quick-buy-modal__price-was')
		);
		priceBlock.appendChild(
			priceNode(selected.sale_price_html, 'bc-quick-buy-modal__price-now')
		);
		priceBlock.appendChild(
			el('span', { className: 'bc-quick-buy-modal__sale-badge', text: labels.sale || 'Sale' })
		);
	} else {
		priceBlock.appendChild(
			priceNode(selected.price_html || '', 'bc-quick-buy-modal__price-now')
		);
	}
	row.appendChild(priceBlock);

	if (config.showPricePerSeed && selected.price_per_seed_html) {
		row.appendChild(
			el('span', { className: 'bc-quick-buy-modal__price-sep', 'aria-hidden': 'true', text: '·' })
		);
		const unit = el('div', { className: 'bc-quick-buy-modal__unit-price' });
		// Price HTML from the API; suffix from page labels (already localised).
		const amount = el('span', { className: 'bc-quick-buy-modal__unit-amount' });
		amount.innerHTML = selected.price_per_seed_html;
		unit.appendChild(amount);
		const suffix = labels.perSeed || 'per seed';
		if (suffix) {
			unit.appendChild(document.createTextNode(` ${suffix}`));
		}
		row.appendChild(unit);
	}

	let note = '';
	if (selected.backorders_allowed && !selected.in_stock) {
		note = labels.backorder || 'Available on backorder';
	} else if (selected.low_stock_remaining !== null && selected.low_stock_remaining !== undefined) {
		note =
			selected.low_stock_remaining > 0
				? `${selected.low_stock_remaining} left`
				: labels.lowStock || 'Low stock';
	}

	if (note) {
		row.appendChild(
			el('span', { className: 'bc-quick-buy-modal__stock-note', text: note })
		);
	}

	priceArea.appendChild(row);
	return priceArea;
}

function fromPriceNode(priceHtml) {
	return priceNode(priceHtml || '', 'bc-quick-buy-modal__from-price price');
}

function buildHeader(options, stockStatus) {
	const header = el('div', { className: 'bc-quick-buy-modal__header' });

	if (options.image && options.image.src) {
		header.appendChild(
			el('img', {
				className: 'bc-quick-buy-modal__image',
				src: options.image.src,
				alt: options.image.alt || options.title || '',
				width: '96',
				height: '96',
			})
		);
	}

	const textChildren = [
		el('h2', {
			className: 'bc-quick-buy-modal__title',
			id: 'bc-quick-buy-modal-title',
			text: options.title || '',
		}),
	];

	if (options.price_html) {
		textChildren.push(fromPriceNode(options.price_html));
	}

	if (stockStatus) {
		textChildren.push(stockStatusNode(stockStatus));
	}

	header.appendChild(el('div', { className: 'bc-quick-buy-modal__heading' }, textChildren));
	return header;
}

export function setLiveMessage(liveRegion, message) {
	if (!liveRegion) {
		return;
	}
	liveRegion.textContent = message || '';
}
